import { useFormikContext } from "formik";
import { ApplyScheduleTemplateBody } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { hasMilestoneComponent } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { Entity } from "@common/types/entity";
import { ScheduleApplyFormValues } from "../scheduleApplyFormHelpers";
import { ScheduleManualApplyDetails } from "./ScheduleManualApplyDetails";
import { ScheduleManualBody } from "./ScheduleManualBody";
import { ScheduleManualModalFrame } from "./ScheduleManualModalFrame";
import { ScheduleManualPreviewPane } from "./ScheduleManualPreviewPane";
import { ScheduleManualLayout } from "./scheduleManualLayout";
import { useMigratedScheduleOption } from "./useMigratedScheduleOption";
import { useScheduleApplyDraft } from "./useScheduleApplyDraft";

const MANUAL_APPLY_SUBTITLE =
    "Review the recognition schedule; expand to full screen to preview it";

interface ScheduleManualApplyContentProps {
    template: ScheduleTemplateResponse | undefined;
    entityIds: number[];
    entitiesById: Map<number, Entity>;
    isExpanded: boolean;
    onExpandedChange: (isExpanded: boolean) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    onSubmit: (payload: ApplyScheduleTemplateBody) => void;
}

/** Apply → Add manually: the source label, entity, accounts and editable rows. */
export const ScheduleManualApplyContent = ({
    template,
    entityIds,
    entitiesById,
    isExpanded,
    onExpandedChange,
    onCancel,
    isSubmitting,
    onSubmit,
}: ScheduleManualApplyContentProps) => {
    const { values } = useFormikContext<ScheduleApplyFormValues>();
    const { draftState, preview, termStart, isApplicable, draftErrorByLayout } =
        useScheduleApplyDraft({
            template,
            entityId: values.entityId,
            amount: 0,
            termStart: "",
        });
    const { resolved, overrides } = draftState;
    const layout: ScheduleManualLayout = isExpanded ? "drawer" : "modal";
    const migrated = useMigratedScheduleOption({
        template,
        entity:
            values.entityId === null
                ? undefined
                : entitiesById.get(values.entityId),
        draftState,
        termStart,
    });
    const manualSourceLabel = values.manualSourceLabel.trim();
    const isMilestonePolicy =
        template !== undefined && hasMilestoneComponent(template.components);
    const canSubmit =
        template !== undefined &&
        values.entityId !== null &&
        manualSourceLabel !== "" &&
        termStart !== "" &&
        migrated.startError === null &&
        isApplicable;

    const submit = () => {
        if (!canSubmit || values.entityId === null || !resolved) {
            return;
        }
        onSubmit({
            sourceType: ScheduleSourceType.manual,
            entityId: values.entityId,
            manualSourceLabel,
            amount: resolved.amount,
            termStart,
            specificDate: resolved.specificDate || undefined,
            overrides:
                Object.keys(overrides).length > 0 ? overrides : undefined,
            ...(migrated.isMigrated ? { isMigratedSchedule: true } : {}),
        });
    };

    return (
        <ScheduleManualModalFrame
            title="Apply schedule"
            subtitle={MANUAL_APPLY_SUBTITLE}
            isExpanded={isExpanded}
            onExpandedChange={onExpandedChange}
            preview={<ScheduleManualPreviewPane preview={preview} />}
            secondaryLabel="Cancel"
            onSecondary={onCancel}
            expandedSecondaryLabel="Back"
            submitLabel="Apply schedule"
            isSubmitDisabled={!canSubmit}
            isSubmitting={isSubmitting}
            onSubmit={submit}
        >
            <ScheduleManualBody
                template={template}
                entityId={values.entityId}
                draft={draftState.draft}
                onDraftChange={draftState.setDraft}
                draftError={migrated.startError ?? draftErrorByLayout[layout]}
                migratedOption={migrated.option}
                isEditable
                areRowsLocked={isMilestonePolicy}
                isSpecificDateEditable
                isHoldingAccountEditable
                layout={layout}
                details={
                    <ScheduleManualApplyDetails
                        entityIds={entityIds}
                        entitiesById={entitiesById}
                        layout={layout}
                    />
                }
            />
        </ScheduleManualModalFrame>
    );
};
